import { Contact } from 'domains/contact/types';

export type OnboardingStatus =
  | 'requires_evaluation_intake_direct_child' // Needs child intake (evaluation and direct mean nothing now)
  | 'requires_evaluation_intake_direct_adult' // Needs adult intake (evaluation and direct mean nothing now)
  | 'ongoing_sessions'; // Has completed their intake and have unlocked full access to portal (dashboard)

export type BaseClient = {
  clientID: string;
  contactID: string;
  clientFirstName: string;
  clientPreferredName?: string;
  clientLastName: string;
  relationshipToClient: Relationship;
};

export type FullClient = {
  clientID: string;
  clientFirstName: string;
  clientMiddleName: string | null;
  clientLastName: string;
  relationshipToClient: Relationship;
  onboardingStatus: OnboardingStatus;
  careStartDate: string;
  clientPreferredName: string | null;
  sex: string;
  genderIdentity: string | null;
  clientDateOfBirth: string;
  primaryLanguage: string;
  specialtiesRequired: string[];
  state?: string;
  primaryContact: Contact;
};

export type Relationship =
  | 'Parent'
  | 'Client'
  | 'Dependent'
  | 'Family Member'
  | 'Legal Guardian'
  | 'Physician'
  | 'Spouse'
  | 'Other';

export enum RelationshipEnum {
  Parent = 'Parent',
  Client = 'Client',
  Dependent = 'Dependent',
  FamilyMember = 'Family Member',
  LegalGuardian = 'Legal Guardian',
  Physician = 'Physician',
  Spouse = 'Spouse',
  Other = 'Other',
}

export enum SubscriberRelationshipEnum {
  Self = 'Self',
  Child = 'Child',
  Spouse = 'Spouse',
  Other = 'Other',
}

/**
 * Having duplicate values for the same relationship, but with different labels
 * This is expected for now.
 * We also expect them to be string, as these values could be alphanumeric
 */
export const CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP = {
  [RelationshipEnum.Parent]: '32',
  [RelationshipEnum.Client]: '18',
  [RelationshipEnum.Dependent]: '19',
  [RelationshipEnum.FamilyMember]: '21',
  [RelationshipEnum.LegalGuardian]: '21',
  [RelationshipEnum.Physician]: '21',
  [RelationshipEnum.Spouse]: '01',
  [RelationshipEnum.Other]: 'G8',
} as const;

export const SUBSCRIBER_RELATIONSHIP_TO_CANDID_ID_MAP = {
  [SubscriberRelationshipEnum.Self]: CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP[RelationshipEnum.Client],
  [SubscriberRelationshipEnum.Child]: CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP[RelationshipEnum.Dependent],
  [SubscriberRelationshipEnum.Spouse]: CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP[RelationshipEnum.Spouse],
  [SubscriberRelationshipEnum.Other]: CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP[RelationshipEnum.Other],
};

export const CANDID_ID_TO_CLIENT_RELATIONSHIP_MAP = Object.fromEntries(
  Object.entries(SUBSCRIBER_RELATIONSHIP_TO_CANDID_ID_MAP).map(([key, value]) => [value, key]),
) as Record<RelationshipEnum, SubscriberRelationshipEnum>;

export type CandidRelationshipToClient =
  (typeof CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP)[keyof typeof CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP];

export const getCandidRelationshipID = (relationship: RelationshipEnum) => {
  return CLIENT_RELATIONSHIP_TO_CANDID_ID_MAP[relationship];
};

export type LanguageCode =
  | 'en'
  | 'es'
  | 'zh'
  | 'tl'
  | 'vi'
  | 'ar'
  | 'fr'
  | 'ko'
  | 'ru'
  | 'de'
  | 'ht'
  | 'hi'
  | 'pt'
  | 'it'
  | 'pl'
  | 'yi'
  | 'ja'
  | 'fa';

export enum PrimaryLanguageEnum {
  English = 'en',
  Arabic = 'ar',
  Chinese = 'zh',
  French = 'fr',
  German = 'de',
  Korean = 'ko',
  Spanish = 'es',
  Tagalog = 'tl',
  Vietnamese = 'vi',
}
